import { type ReactNode } from 'react'
import { Box, Divider, Paper, Typography } from '@mui/material'
import FilterToolbar, { type FilterToolbarField } from './FilterToolbar'
import FilterIcon from '@assets/DatePickersIcons/FilterIcon.svg'
import { useAppTheme } from '@/theme/ThemeContext'
import { type DateRangeValue } from '@/components/DatePickers/dateRange'
import { useRangeLimits } from '@/components/DatePickers/useRangeLimits'

export type DateRangeFilterValue = DateRangeValue

type DateRangeFilterCardProps = {
  range: DateRangeFilterValue
  onRangeChange: (range: DateRangeFilterValue) => void
  onSearch?: (from?: string, to?: string) => void
  loading?: boolean
  disabled?: boolean
  title?: string
  extraFields?: FilterToolbarField[]
  footer?: ReactNode
  compact?: boolean
  /** Readings and other past data have no future days. */
  disableFuture?: boolean
}

const formatDateForApi = (date: Date | null) => {
  if (!date) return undefined
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export default function DateRangeFilterCard({
  range,
  onRangeChange,
  onSearch,
  disabled = false,
  title = 'סינון לפי תאריכים',
  extraFields = [],
  footer,
  compact = false,
  disableFuture = true,
}: DateRangeFilterCardProps) {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const colors = {
    text: isDarkMode ? '#BBD7FF' : '#003F61',
    divider: isDarkMode ? '#4B6FB1' : '#E6ECF2',
    cardBackground: isDarkMode ? '#1D325A' : '#FAFBFD',
    cardBorder: isDarkMode ? '#344E7A' : '#E6ECF2',
  }
  const limits = useRangeLimits(range, {})
  // Keep search available while a request is in flight so the user can
  // submit a changed range again; loading is represented by the parent UI.
  const searchDisabled = disabled || limits.hasError || !range.from || !range.to
  const fieldWidth = compact
    ? 'clamp(150px, 10vw, 190px)'
    : 'clamp(120px, 11vw, 190px)'

  const dateFields: FilterToolbarField[] = [
    {
      name: 'from-date',
      type: 'date',
      label: 'מתאריך',
      value: range.from,
      width: fieldWidth,
      disableFuture,
      onChange: value => onRangeChange({ ...range, from: value }),
      ...limits.from,
    },
    {
      name: 'date-separator',
      type: 'custom',
      render: (
        <Typography
          sx={{
            color: isDarkMode ? '#BBD7FF' : '#003F61',
            fontFamily: 'Rubik, sans-serif',
            flexShrink: 0,
            px: 0.25,
          }}
        >
          —
        </Typography>
      ),
    },
    {
      name: 'to-date',
      type: 'date',
      label: 'עד תאריך',
      value: range.to,
      width: fieldWidth,
      disableFuture,
      onChange: value => onRangeChange({ ...range, to: value }),
      ...limits.to,
    },
  ]

  return (
    <Box
      dir="rtl"
      sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, md: 2 },
          borderRadius: '20px',
          border: `1px solid ${colors.cardBorder}`,
          bgcolor: colors.cardBackground,
          color: colors.text,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
          <img
            src={FilterIcon}
            alt=""
            style={{
              width: 'clamp(16px, 1.2vw, 20px)',
              height: 'clamp(16px, 1.2vw, 20px)',
              filter: isDarkMode
                ? 'brightness(0) saturate(100%) invert(78%) sepia(44%) saturate(511%) hue-rotate(180deg)'
                : 'none',
            }}
          />
          <Typography
            sx={{
              color: colors.text,
              fontWeight: 700,
              fontFamily: 'Rubik, sans-serif',
            }}
          >
            {title}
          </Typography>
        </Box>

        <Divider sx={{ borderColor: colors.divider, mb: { xs: 1.5, md: 2 } }} />

        {extraFields.length > 0 ? (
          <Box sx={{ mb: 1.25 }}>
            <FilterToolbar
              fields={extraFields}
              onSearch={() => undefined}
              hideSearch
              compact={compact}
            />
          </Box>
        ) : null}

        <FilterToolbar
          fields={dateFields}
          onSearch={() => {
            if (searchDisabled) return
            onSearch?.(formatDateForApi(range.from), formatDateForApi(range.to))
          }}
          disabled={searchDisabled}
          compact={compact}
        />

        {footer}
      </Paper>
    </Box>
  )
}
